import {useAgencySubbrandsQuery} from "@/api/agencyApi";
import {useCurrentCompany} from "@/api/companyApi";
import CampaignIcon from "@/assets/colorable/campaign.svg?react";
import ChairIcon from "@/assets/colorable/chair.svg?react";
import CircularArrowsIcon from "@/assets/colorable/circular_arrows.svg?react";
import InfinityIcon from "@/assets/colorable/infinity.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import MailPlusIcon from "@/assets/colorable/mail_plus.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import RefluencedCreditsIcon from "@/assets/colorable/refluenced_credits.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import SourceContentIcon from "@/assets/colorable/source_content.svg?react";
import {Tooltip, type PlacesType} from "@/components/Common/Tooltip";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useAgencySeats, useCampaignsLeft, useIsAgency, useIsSubbrand} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {INFINITY_THRESHOLD} from "@/store/constants";
import {isMoreBuyoutsModalOpenAtom, isRequestMoreResourcesModalOpenAtom, settingsModalAtom} from "@/store/jotaiAtoms";
import type {TFloatingBuyouts} from "@/store/types";
import {useLocation, useNavigate, useParams} from "@tanstack/react-router";
import dayjs from "dayjs";
import {useSetAtom} from "jotai";
import {FC} from "react";
import {useTranslation} from "react-i18next";

const PlanDetails: FC<{
  place?: PlacesType;
  type: TFloatingBuyouts;
}> = ({place = "top", type}) => {
  const {t} = useTranslation("common");
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {agencyId: urlAgencyId, companyId: urlCompanyId} = useParams({strict: false});
  const isOnInvoicesPage = pathname.includes("/invoices");
  const isOnAgencyOnlyContext = !!urlAgencyId && !urlCompanyId;
  const {credits, isOwnProfile, remainingBuyouts, packageIncludedBuyouts, validUntil} = usePackageLimits();
  const campaignsLeft = useCampaignsLeft();
  const seats = useAgencySeats();
  const isAgency = useIsAgency();
  const isSubbrand = useIsSubbrand();
  const {data: company} = useCurrentCompany();
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const {data: subBrands} = useAgencySubbrandsQuery({
    agencyId: Number(urlAgencyId ?? 0),
    queryOptions: {enabled: isOnAgencyOnlyContext},
  });
  const firstSubBrandId = subBrands?.[0]?.id;

  const isRequest =
    isSubbrand && ((type === "buyouts" && remainingBuyouts === 0) || (type === "campaigns" && campaignsLeft === 0));
  // TODO: add casl
  const setMoreBuyoutsModalOpen = useSetAtom(isMoreBuyoutsModalOpenAtom);
  const openMoreModal = useSetAtom(isRequestMoreResourcesModalOpenAtom);
  const canNavigateToInvoice = !isOnInvoicesPage && (!isOnAgencyOnlyContext || !!firstSubBrandId);

  if (type === "credits") {
    return (
      <div
        onClick={() => {
          if (!canNavigateToInvoice) return;

          if (isAgency || urlAgencyId) {
            const agencyId = urlAgencyId ?? company?.agency.id;
            const companyId = urlCompanyId ?? firstSubBrandId ?? company?.id;
            if (!agencyId || !companyId) return;
            navigate({
              to: "/agency/$agencyId/subbrand/$companyId/invoices",
              params: {agencyId, companyId},
            });
            return;
          }

          if (!company?.id) return;
          navigate({to: "/company/$companyId/invoices", params: {companyId: company.id}});
        }}
        className={clsxm(
          "border-neutral-white-dark divide-neutral-grey-lightest flex size-fit origin-bottom-left cursor-pointer flex-col divide-y rounded-md border bg-white shadow-xl drop-shadow-lg transition-all duration-200 ease-in-out",
          !canNavigateToInvoice && "cursor-default",
        )}>
        <div className="flex min-h-9.5 items-center justify-between gap-2 px-1.5 py-2 sm:px-3">
          <div className="flex items-center gap-2">
            <div data-tooltip-id="refluenced-credits-tooltip" onClick={e => e.stopPropagation()}>
              <InfoIcon className="text-primary-dark-solid size-3.5 shrink-0 cursor-pointer" />
            </div>
            <div className="flex items-center gap-2">
              <RefluencedCreditsIcon className="text-neutral-black size-3.5 shrink-0" />
              <span className="text-neutral-black text-sm font-semibold">{credits}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Can I="update" a="CompanyCredits">
              <button
                onClick={e => {
                  e.stopPropagation();

                  if (urlAgencyId) {
                    setSettingsModal({
                      scope: {id: Number(urlAgencyId), type: "agency"},
                      scrollTo: "credits",
                      section: "admin_billing",
                    });
                    return;
                  }

                  if (!company?.id) return;

                  if (company.agency?.multiple_brands) {
                    setSettingsModal({
                      scope: {id: company.agency.id, type: "agency"},
                      scrollTo: "credits",
                      section: "admin_billing",
                    });
                    return;
                  }

                  setSettingsModal({
                    scope: {id: company.id, type: "brand"},
                    scrollTo: "credits",
                    section: "admin_billing",
                  });
                }}>
                <PlusIcon className="text-neutral-black hover:text-primary-dark-solid size-3 shrink-0 transition-all duration-150" />
              </button>
            </Can>
            {canNavigateToInvoice && (
              <RightChevronIcon className="text-neutral-black hover:text-primary-dark-solid size-2.5 shrink-0 transition-all duration-150" />
            )}
          </div>
          <Tooltip
            id="refluenced-credits-tooltip"
            clickable
            place={place}
            className="z-50 max-w-51.5 rounded-lg bg-white p-3 shadow-md"
            opacity={1}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <InfoIcon className="text-neutral-grey-dark size-3.5 shrink-0" />
                <span className="text-neutral-black text-sm font-semibold">{t("refluenced_credits")}</span>
              </div>
              <p className="text-neutral-grey-dark text-2xs text-left leading-3.5 font-normal">
                {t("refluenced_credits_description")}
              </p>
              <p className="text-neutral-grey-dark text-2xs text-left leading-3.5 font-normal">
                {t("refluenced_credits_failure_to_complete")}
              </p>
              <p className="text-neutral-grey-dark text-2xs text-left leading-3.5 font-normal">
                {t("refluenced_credits_sales_adjustments")}
              </p>
              <p className="text-neutral-grey-dark text-2xs text-left leading-3.5 font-normal">
                {t("refluenced_credits_disclaimer")}
              </p>
            </div>
          </Tooltip>
        </div>
        <span className="text-neutral-grey-dark text-2xs px-1.5 py-2 font-normal sm:px-3">
          {t("refluenced_credits")}
        </span>
      </div>
    );
  }

  return (
    <div className="border-neutral-white-dark divide-neutral-grey-lightest flex size-fit origin-bottom-left flex-col divide-y rounded-md border bg-white shadow-xl drop-shadow-lg transition-all duration-500 ease-in-out">
      <div className="flex min-h-9.5 items-center justify-between px-1.5 py-2 sm:px-3">
        {type === "buyouts" && (
          <div className="flex items-center gap-2" data-tooltip-id="remaining-buyouts" data-tip>
            <Tooltip id="remaining-buyouts" place={place} variant="dark" delayShow={500}>
              {t("contentBuyoutsRemaining")}
            </Tooltip>
            <SourceContentIcon className="text-neutral-black size-3.5" />
            {remainingBuyouts > INFINITY_THRESHOLD ? (
              <InfinityIcon className="text-neutral-black h-4 w-5" />
            ) : (
              <span className="text-neutral-black text-sm font-semibold">{remainingBuyouts}</span>
            )}
          </div>
        )}
        {type === "seats" && (
          <div className="flex items-center gap-2" data-tooltip-id="remaining-seats" data-tip>
            <Tooltip id="remaining-seats" place={place} variant="dark" delayShow={500}>
              {t("seats_remaining")}
            </Tooltip>
            <ChairIcon className="text-neutral-black size-3.5" />
            {!!seats && seats > INFINITY_THRESHOLD ? (
              <InfinityIcon className="text-neutral-black h-4 w-5" />
            ) : (
              <span className="text-neutral-black text-sm font-semibold">{seats}</span>
            )}
          </div>
        )}
        {type === "campaigns" && (
          <div className="flex items-center gap-2" data-tooltip-id="remaining-campaigns" data-tip>
            <Tooltip id="remaining-campaigns" place={place} variant="dark" delayShow={500}>
              {t("campaigns_remaining")}
            </Tooltip>
            <CampaignIcon className="text-neutral-black size-3.5" />
            {!!campaignsLeft && campaignsLeft! > INFINITY_THRESHOLD ? (
              <InfinityIcon className="text-neutral-black h-4 w-5" />
            ) : (
              <span className="text-neutral-black text-sm font-semibold">{campaignsLeft}</span>
            )}
          </div>
        )}
        {isOwnProfile && (
          <button
            className="group hover:bg-neutral-white-dark rounded-md transition-all duration-150"
            onClick={() => {
              if (isRequest) {
                return openMoreModal({
                  show: true,
                  type,
                });
              }

              if (type === "buyouts") {
                setMoreBuyoutsModalOpen({show: true, type: "EXTRA_BUYOUTS"});
              } else if (type === "seats") {
                setMoreBuyoutsModalOpen({show: true, type: "EXTRA_SEATS"});
              } else {
                setMoreBuyoutsModalOpen({
                  show: true,
                  type: "EXTRA_CAMPAIGNS",
                });
              }
            }}>
            {isRequest && <MailPlusIcon className="text-primary-dark-solid size-4 transition-all duration-150" />}
            {!isSubbrand && (
              <PlusIcon className="text-neutral-black group-hover:text-primary-dark-solid size-4 transition-all duration-150" />
            )}
          </button>
        )}
      </div>
      <div data-tooltip-id="reset-buyouts" className="flex items-center justify-start gap-2 px-1.5 py-2 sm:px-3">
        <Tooltip id="reset-buyouts" place="right" variant="light" delayShow={500}>
          {t("buyoutsWillBeResetted", {buyoutCount: packageIncludedBuyouts})}
        </Tooltip>
        <CircularArrowsIcon className="text-neutral-grey-dark size-2.5" />
        <span className="text-neutral-grey-dark text-2xs">{dayjs(validUntil).format("DD. MMMM YYYY.")}</span>
      </div>
    </div>
  );
};

export default PlanDetails;
